import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { mapIsoStringtoUtcObject } from '~/modules/common/dates/convert';
import { MAX_DATE, MIN_DATE } from '~/modules/common/const';
import ChevronDateButtons from '~/modules/common/components/ChevronDateButtons';
import DateRange from '~/modules/common/components/DateRange';
import PeriodStartDatePicker from './PeriodStartDatePicker';
import PeriodStartDateLabel from './PeriodStartDateLabel';

export const DISPLAY_VARIANT = {
  DATE: 'date',
  DATE_RANGE: 'dateRange'
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  label: {
    ...theme.typography.h6,
    lineHeight: `${theme.spacing(3)}px`,
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.body1.fontSize
    },
    padding: theme.spacing(0, 0.5),
    margin: theme.spacing(0, -0.5),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    '&:hover': {
      textDecoration: 'none',
      backgroundColor: theme.palette.action.hover,
      '@media (hover: none)': {
        backgroundColor: 'transparent'
      }
    }
  },
  dateRangeIndicator: {
    flexGrow: 0,
    flexShrink: 0,
    color: theme.palette.text.secondary
  }
}));

const dateRangeStyles = makeStyles(theme => ({
  date: {
    ...theme.typography.h6,
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.body1.fontSize,
      display: 'inline-flex'
    }
  },
  range: {
    alignSelf: 'center',
    textAlign: 'left'
  },
  spacer: {
    ...theme.typography.caption,
    color: 'inherit',
    lineHeight: 'inherit'
  }
}));

const useButtonStyles = makeStyles(theme => ({
  previousButton: {
    margin: theme.spacing(-1, 0, -1, 0)
  }
}));

export const PeriodStartDateSelector = ({
  classes: classesOverride,
  onPreviousClick,
  onNextClick,
  dateRange,
  displayVariant = DISPLAY_VARIANT.DATE_RANGE,
  setDate,
  scale,
  hideDatePicker = false
}) => {
  const classes = useStyles({
    classes: classesOverride
  });
  const buttonStyles = useButtonStyles();
  const dateRangeClasses = dateRangeStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = useCallback(event => setAnchorEl(event.currentTarget), [
    setAnchorEl
  ]);

  const handleClose = useCallback(() => setAnchorEl(null), [setAnchorEl]);

  const handleDateChange = useCallback(
    value => {
      setDate(value);
      handleClose();
    },
    [handleClose, setDate]
  );

  const disableLeftButton =
    mapIsoStringtoUtcObject(dateRange.startDate.toISODate()) <=
    mapIsoStringtoUtcObject(MIN_DATE);
  const disableRightButton =
    mapIsoStringtoUtcObject(dateRange.endDate.toISODate()) >=
    mapIsoStringtoUtcObject(MAX_DATE);

  return (
    <div className={classes.root}>
      <ChevronDateButtons
        onPreviousClick={onPreviousClick}
        onNextClick={onNextClick}
        disableLeftButton={disableLeftButton}
        disableRightButton={disableRightButton}
        classes={buttonStyles}
      />
      {!hideDatePicker && (
        <>
          <ButtonBase
            data-qe-id="PeriodStartDateSelector"
            className={classes.label}
            aria-owns={isOpen ? 'period-start-date-selector-menu' : undefined}
            aria-haspopup="true"
            onClick={handleClick}
            focusRipple
          >
            {displayVariant === DISPLAY_VARIANT.DATE ? (
              <PeriodStartDateLabel
                scale={scale}
                dateRange={dateRange}
                dateRangeClasses={dateRangeClasses}
              />
            ) : (
              <DateRange
                start={dateRange.startDate}
                end={dateRange.endDate}
                classes={dateRangeClasses}
              />
            )}
            <ArrowDropDownIcon className={classes.dateRangeIndicator} />
          </ButtonBase>
          <PeriodStartDatePicker
            menuId="period-start-date-selector-menu"
            anchorEl={anchorEl}
            scale={scale}
            dateRange={dateRange}
            onClose={handleClose}
            onChange={handleDateChange}
          />
        </>
      )}
    </div>
  );
};

PeriodStartDateSelector.propTypes = {
  classes: PropTypes.object,
  onPreviousClick: PropTypes.func.isRequired,
  onNextClick: PropTypes.func.isRequired,
  dateRange: PropTypes.object.isRequired,
  scale: PropTypes.string,
  setDate: PropTypes.func.isRequired,
  hideDatePicker: PropTypes.bool,
  displayVariant: PropTypes.oneOf(Object.values(DISPLAY_VARIANT))
};

export default PeriodStartDateSelector;
